import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  MapPin, 
  ArrowRight, 
  Building2, 
  Search,
  Star,
} from 'lucide-react';

// All 50 US States
const ALL_STATES = [
  { name: 'Alabama', abbr: 'AL' },
  { name: 'Alaska', abbr: 'AK' },
  { name: 'Arizona', abbr: 'AZ' },
  { name: 'Arkansas', abbr: 'AR' },
  { name: 'California', abbr: 'CA', featured: true, page: '/california-social-worker-jobs' },
  { name: 'Colorado', abbr: 'CO' },
  { name: 'Connecticut', abbr: 'CT' },
  { name: 'Delaware', abbr: 'DE' },
  { name: 'Florida', abbr: 'FL', featured: true, page: '/florida-social-worker-jobs' },
  { name: 'Georgia', abbr: 'GA' },
  { name: 'Hawaii', abbr: 'HI' },
  { name: 'Idaho', abbr: 'ID' },
  { name: 'Illinois', abbr: 'IL' },
  { name: 'Indiana', abbr: 'IN' },
  { name: 'Iowa', abbr: 'IA' },
  { name: 'Kansas', abbr: 'KS' },
  { name: 'Kentucky', abbr: 'KY' },
  { name: 'Louisiana', abbr: 'LA' },
  { name: 'Maine', abbr: 'ME' },
  { name: 'Maryland', abbr: 'MD' },
  { name: 'Massachusetts', abbr: 'MA' },
  { name: 'Michigan', abbr: 'MI', featured: true, page: '/michigan-social-worker-jobs' },
  { name: 'Minnesota', abbr: 'MN' },
  { name: 'Mississippi', abbr: 'MS' },
  { name: 'Missouri', abbr: 'MO' },
  { name: 'Montana', abbr: 'MT' },
  { name: 'Nebraska', abbr: 'NE' },
  { name: 'Nevada', abbr: 'NV', featured: true, page: '/nevada-social-worker-jobs' },
  { name: 'New Hampshire', abbr: 'NH' },
  { name: 'New Jersey', abbr: 'NJ' },
  { name: 'New Mexico', abbr: 'NM' },
  { name: 'New York', abbr: 'NY', featured: true, page: '/new-york-social-worker-jobs' },
  { name: 'North Carolina', abbr: 'NC' },
  { name: 'North Dakota', abbr: 'ND' },
  { name: 'Ohio', abbr: 'OH' },
  { name: 'Oklahoma', abbr: 'OK' },
  { name: 'Oregon', abbr: 'OR' },
  { name: 'Pennsylvania', abbr: 'PA' },
  { name: 'Rhode Island', abbr: 'RI' },
  { name: 'South Carolina', abbr: 'SC' },
  { name: 'South Dakota', abbr: 'SD' },
  { name: 'Tennessee', abbr: 'TN' },
  { name: 'Texas', abbr: 'TX', featured: true, page: '/texas-social-worker-jobs' },
  { name: 'Utah', abbr: 'UT' },
  { name: 'Vermont', abbr: 'VT' },
  { name: 'Virginia', abbr: 'VA' },
  { name: 'Washington', abbr: 'WA' },
  { name: 'West Virginia', abbr: 'WV' },
  { name: 'Wisconsin', abbr: 'WI' },
  { name: 'Wyoming', abbr: 'WY' },
];

const FEATURED_STATES_DATA = [
  { 
    name: 'Texas', 
    abbr: 'TX', 
    cities: ['Austin', 'Houston', 'Dallas', 'San Antonio', 'Fort Worth', 'Waco', 'Temple'],
    description: 'Our headquarters state with strong healthcare and educational systems.',
    page: '/texas-social-worker-jobs'
  },
  { 
    name: 'California', 
    abbr: 'CA', 
    cities: ['Los Angeles', 'San Francisco', 'San Diego', 'Sacramento', 'San Jose'],
    description: 'High demand in behavioral health and community services.',
    page: '/california-social-worker-jobs'
  },
  { 
    name: 'Florida', 
    abbr: 'FL', 
    cities: ['Miami', 'Orlando', 'Tampa', 'Jacksonville', 'Fort Lauderdale'],
    description: 'Growing healthcare and hospice industry opportunities.',
    page: '/florida-social-worker-jobs'
  },
  { 
    name: 'New York', 
    abbr: 'NY', 
    cities: ['New York City', 'Buffalo', 'Rochester', 'Albany', 'Syracuse'],
    description: 'Major hospital systems and school social work positions.',
    page: '/new-york-social-worker-jobs'
  },
  { 
    name: 'Michigan', 
    abbr: 'MI', 
    cities: ['Detroit', 'Grand Rapids', 'Ann Arbor', 'Lansing', 'Flint'],
    description: 'Hospital and community agency positions across the state.',
    page: '/michigan-social-worker-jobs'
  },
  { 
    name: 'Nevada', 
    abbr: 'NV', 
    cities: ['Las Vegas', 'Reno', 'Henderson', 'North Las Vegas', 'Sparks'],
    description: 'Growing behavioral health and medical social work opportunities.',
    page: '/nevada-social-worker-jobs'
  },
];

const Locations = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredStates, setFilteredStates] = useState(ALL_STATES);

  useEffect(() => {
    // Set SEO meta tags
    document.title = "Social Work Jobs by State | All 50 States | INeedASocialWorker";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = "Find social work jobs in all 50 states. Browse LMSW and LCSW positions in Texas, California, Florida, New York, Michigan, Nevada, and nationwide.";
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      setFilteredStates(
        ALL_STATES.filter(
          state => state.name.toLowerCase().includes(query) || state.abbr.toLowerCase().includes(query)
        )
      );
    } else {
      setFilteredStates(ALL_STATES);
    }
  }, [searchQuery]);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-[#0F2E24] py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
            <MapPin className="w-5 h-5 text-secondary" />
            <span className="text-secondary font-medium">All 50 States</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white mb-4">
            Explore Social Work Jobs by State
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-8">
            Find licensed social worker positions across the United States. Browse by state to find LMSW, LCSW, and BSW opportunities near you.
          </p>
          
          {/* State Search */}
          <div className="max-w-md mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search states..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 bg-white"
                data-testid="state-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured States with Dedicated Pages */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-secondary/10 px-4 py-2 rounded-full mb-4">
              <Star className="w-4 h-4 text-secondary fill-secondary" />
              <span className="text-secondary font-medium text-sm">Featured States</span>
            </div>
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4">
              States with Dedicated Job Pages
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These states have high demand for licensed social workers. Explore specialized job listings and resources.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURED_STATES_DATA.map((state) => (
              <div 
                key={state.abbr}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="bg-primary/5 px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-xl font-bold text-gray-900">
                      {state.name}
                    </h3>
                    <span className="text-sm text-secondary font-medium bg-secondary/10 px-2 py-1 rounded">{state.abbr}</span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 text-sm mb-4">{state.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {state.cities.slice(0, 4).map((city) => (
                      <Link
                        key={city}
                        to={`/jobs?location=${encodeURIComponent(city + ', ' + state.abbr)}`}
                        className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-primary hover:text-white transition-colors"
                      >
                        {city}
                      </Link>
                    ))}
                    {state.cities.length > 4 && (
                      <span className="text-xs px-2 py-1 text-gray-400">
                        +{state.cities.length - 4} more
                      </span>
                    )}
                  </div>
                  <Link to={state.page}>
                    <Button className="w-full bg-primary hover:bg-primary-hover text-white" data-testid={`browse-${state.abbr}-jobs`}>
                      Explore {state.name} Jobs
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All 50 States Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4">
              Browse All 50 States
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Click any state to view available social work positions. New jobs are added daily.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filteredStates.map((state) => (
              <Link
                key={state.abbr}
                to={state.page || `/jobs?location=${encodeURIComponent(state.name)}`}
                className={`flex items-center gap-2 p-3 rounded-lg transition-all ${
                  state.featured 
                    ? 'bg-secondary/10 border border-secondary/30 hover:bg-secondary/20' 
                    : 'bg-gray-50 hover:bg-primary hover:text-white border border-transparent hover:border-primary'
                } group`}
              >
                <MapPin className={`w-4 h-4 flex-shrink-0 ${state.featured ? 'text-secondary' : 'text-primary group-hover:text-white'}`} />
                <div className="min-w-0">
                  <p className={`font-medium text-sm truncate ${state.featured ? 'text-gray-900' : ''}`}>{state.name}</p>
                  <p className={`text-xs ${state.featured ? 'text-secondary' : 'text-gray-500 group-hover:text-gray-200'}`}>{state.abbr}</p>
                </div>
                {state.featured && <Star className="w-3 h-3 text-secondary fill-secondary ml-auto flex-shrink-0" />}
              </Link>
            ))}
          </div>

          {filteredStates.length === 0 && (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No states match "{searchQuery}"</p>
            </div>
          )}
        </div>
      </section>

      {/* Texas Headquarters Section */}
      <section className="py-16 bg-gradient-to-br from-secondary/10 to-primary/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
            <Building2 className="w-5 h-5 text-primary" />
            <span className="text-primary font-medium">Headquartered in Texas</span>
          </div>
          <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4">
            Texas-Based. Nationwide Reach.
          </h2>
          <p className="text-gray-700 text-lg mb-8">
            Founded in Texas, INeedASocialWorker was built to address the growing demand for qualified social workers. Our regional expertise provides strong healthcare and educational industry insight while our expanding platform supports hiring nationwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/texas-social-worker-jobs">
              <Button className="bg-primary hover:bg-primary-hover text-white px-8">
                Explore Texas Jobs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white px-8">
                About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Ready to Find Your Next Opportunity?
          </h2>
          <p className="text-gray-300 mb-8">
            Join thousands of social workers who have found their next career through INeedASocialWorker.com
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-secondary hover:bg-secondary-hover text-white px-8" data-testid="locations-signup-cta">
                Create Free Account
              </Button>
            </Link>
            <Link to="/for-employers">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary px-8">
                For Employers
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Locations;
